import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, ChevronRight, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useEsAdmin } from "@/hooks/useEsAdmin";

const ROLES_INTERNOS = [
  "ADMIN_SICEN",
  "GERENTE_CALIDAD",
  "AUXILIAR_CALIDAD",
  "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO",
  "RESPONSABLE_TELECOM",
  "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA",
  "AUDITOR_INTERNO",
  "DIRECCION",
  "FACTURACION",
] as const;

interface UsuarioCreadoResponse {
  id: string;
  correo: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  rol: string;
  passwordInicial: string;
}

function errorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const resp = (err as { response?: { data?: { detail?: string; message?: string } } }).response;
    if (resp?.data?.detail) return resp.data.detail;
    if (resp?.data?.message) return resp.data.message;
  }
  if (err instanceof Error) return err.message;
  return "Error desconocido";
}

export function UsuariosInternosAltaPage() {
  const navigate = useNavigate();
  const esAdmin = useEsAdmin();
  // D-04/IAM: cuentas ADMIN_SICEN solo las crea otro admin (escudo también en backend).
  const rolesDisponibles = ROLES_INTERNOS.filter((r) => esAdmin || r !== "ADMIN_SICEN");
  const [correo, setCorreo] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellidoPaterno, setApellidoPaterno] = useState("");
  const [apellidoMaterno, setApellidoMaterno] = useState("");
  const [rol, setRol] = useState("");
  const [notificar, setNotificar] = useState(true);

  const [creado, setCreado] = useState<UsuarioCreadoResponse | null>(null);
  const [copiado, setCopiado] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<UsuarioCreadoResponse>("/api/iam/usuarios", {
        correo,
        nombre,
        apellidoPaterno,
        apellidoMaterno: apellidoMaterno || null,
        rol,
        notificar,
      });
      return data;
    },
    onSuccess: (data) => setCreado(data),
  });

  async function copiarPwd() {
    if (!creado) return;
    await navigator.clipboard.writeText(creado.passwordInicial);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  if (creado) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/usuarios"
          className="inline-flex items-center gap-1 text-sm text-fg-muted transition hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" /> Volver a usuarios
        </Link>
        <div className="rounded-lg border border-estado-ok/25 bg-estado-ok-bg p-4">
          <h1 className="text-lg font-semibold text-estado-ok">Usuario creado</h1>
          <p className="mt-1 text-sm text-estado-ok">
            La cuenta de <strong>{creado.correo}</strong> ya está activa en Keycloak con rol{" "}
            <strong>{creado.rol}</strong>.
          </p>
        </div>

        <div className="rounded-lg border bg-surface p-4 shadow-card">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-estado-warn" />
            <h2 className="text-base font-semibold text-fg">Password inicial</h2>
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            Se mostrará una sola vez. El usuario deberá cambiarla obligatoriamente en su primer login
            (UPDATE_PASSWORD).
            {notificar
              ? " También se envió por correo a la dirección registrada."
              : " No se envió por correo — entrégala por canal seguro."}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 rounded border bg-surface-2 px-3 py-2 font-mono text-sm">
              {creado.passwordInicial}
            </code>
            <button
              type="button"
              onClick={copiarPwd}
              className="cursor-pointer rounded border bg-surface px-3 py-2 text-xs transition hover:bg-surface-2"
            >
              {copiado ? "Copiado ✓" : "Copiar"}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setCreado(null);
              setCorreo("");
              setNombre("");
              setApellidoPaterno("");
              setApellidoMaterno("");
              setRol("");
            }}
            className="cursor-pointer rounded border px-3 py-1.5 text-sm transition hover:bg-surface-2"
          >
            Dar de alta otro
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/usuarios")}
            className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover"
          >
            Ir al listado
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link
        to="/admin/usuarios"
        className="inline-flex items-center gap-1 text-sm text-fg-muted transition hover:text-fg"
      >
        <ArrowLeft className="h-4 w-4" /> Volver a usuarios
      </Link>

      <header>
        <h1 className="text-2xl font-semibold text-fg">Nuevo usuario interno</h1>
        <p className="text-sm text-fg-muted">
          El backend genera la contraseña inicial y crea la cuenta en Keycloak con el rol seleccionado.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (mutation.isPending) return;
          mutation.mutate();
        }}
        className="space-y-4 rounded-lg border bg-surface p-4 shadow-card"
      >
        <div className="grid gap-3 md:grid-cols-2">
          <label className="block">
            <span className="text-xs uppercase text-fg-subtle">Correo *</span>
            <input
              type="email"
              required
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
              placeholder="usuario@npmx.org"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase text-fg-subtle">Rol *</span>
            <select
              required
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            >
              <option value="">— elegir rol —</option>
              {rolesDisponibles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase text-fg-subtle">Nombre *</span>
            <input
              type="text"
              required
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase text-fg-subtle">Apellido paterno *</span>
            <input
              type="text"
              required
              value={apellidoPaterno}
              onChange={(e) => setApellidoPaterno(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase text-fg-subtle">Apellido materno</span>
            <input
              type="text"
              value={apellidoMaterno}
              onChange={(e) => setApellidoMaterno(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={notificar}
            onChange={(e) => setNotificar(e.target.checked)}
            className="h-4 w-4"
          />
          Enviar correo de bienvenida con la password inicial
        </label>

        {mutation.error && (
          <div className="flex items-start gap-2 rounded border border-estado-err/25 bg-estado-err-bg p-3 text-sm text-estado-err">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <span>{errorMessage(mutation.error)}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Link to="/admin/usuarios" className="rounded border px-3 py-1.5 text-sm transition hover:bg-surface-2">
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {mutation.isPending ? "Creando…" : "Crear usuario"}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
